import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, lastValueFrom, Observable } from "rxjs";
import { Subject } from "rxjs";
import { map } from "rxjs/operators";
import { cloneDeep } from "lodash";

import { Brand } from "../models/brand";
import { Shape } from "../models/shape";
import { Cigar } from "../models/cigar";
import { DisplayloadingService } from "./displayloading.service";
import { environment } from "src/environments/environment";
import { Router } from "@angular/router";
import { HumidorEntry } from "../models/humidor_entry";
import { Country } from "../models/country";
import { Drink } from "../models/drink";
import { Aroma } from "../models/aroma";
import { PopoverController } from "@ionic/angular";
import { YesNoPopupComponent } from "../popovers/yes-no-popup/yes-no-popup.component";
import { Humidor } from "../models/humidor";
import { AuthService } from "./auth.service";
import { HumidorPopoverLoadingComponent } from "../popovers/humidor-popover-loading/humidor-popover-loading.component";

@Injectable({
  providedIn: "root",
})

/**
 * @description
 * Dieser Service bietet Methoden zur Aktualisierung des Humidor-Inhalts an, sowie
 * Methoden, die neue Zigarren in der Datenbank registrieren können und diese dann ebenfalls
 * in den Humidor legen
 */
export class HumidorService {
  private api = environment.apiUrl;
  private entriesUpdated = new Subject<HumidorEntry[]>();
  private cigarsSearched = new Subject<Cigar[]>();
  private humEntries: HumidorEntry[] = [];
  private loadingPop: any;
  private percLoaded = new BehaviorSubject<number>(0);

  constructor(
    private http: HttpClient,
    private ls: DisplayloadingService,
    private router: Router,
    private popc: PopoverController,
    private as: AuthService,
  ) {}

  /**
   *
   * @returns alle Markenobjekte aus der Datenbank
   */
  public getAllBrands(): Observable<Brand[]> {
    return this.http.get<Brand[]>(`${this.api}/get_brands.php`);
  }
  /**
   *
   * @returns alle Zigarrenformatobjekte aus der Datenbank
   */
  public getAllShapes(): Observable<Shape[]> {
    return this.http.get<Shape[]>(`${this.api}/get_shapes.php`);
  }
  /**
   *
   * @returns alle Länderobjekte aus der Datenbank
   */
  public getAllCountries(): Observable<Country[]> {
    return this.http.get<Country[]>(`${this.api}/get_countries.php`);
  }

  /**
   * @returns alle Getränke aus der Datenbank
   */
  public getAllDrinks(): Observable<Drink[]> {
    return this.http.get<Drink[]>(`${this.api}/get_drinks.php`);
  }

  /**
   *
   * @returns alle Aromen aus der Datenbank
   */
  public getAllAromas(): Observable<Aroma[]> {
    return this.http.get<Aroma[]>(`${this.api}/get_aromas.php`);
  }

  /**
   * @param searchString Suchstring aus addCigar-Formular
   * @description
   * holt alle anhand von Suchstring gefundenen Zigarrenobjekte aus der Datenbank
   * mapped sie und aktualisiert das Subject
   */
  public getCigars(searchString: string) {
    this.http
      .post<any[]>(`${this.api}/get_cigars.php`, { searchString: searchString })
      .pipe(
        map((postData) => {
          return postData.map((cigar) => {
            return {
              id: cigar.id,
              name: cigar.name,
              brand: {
                id: cigar.brand_id,
                name: cigar.brand_name,
                since: 0,
              },
              length: cigar.length,
              diameter: cigar.diameter,
              construction: cigar.construction, //Enum in DB longfiller, shortfiller, mediumfiller
              shape: {
                id: cigar.shape_id,
                name: cigar.shape_name,
                length_min: cigar.length_min,
                length_max: cigar.length_max,
                diameter_min: cigar.diameter_min,
                diameter_max: cigar.diameter_max,
              },
              ring: cigar.ring,
              origin: cigar.origin,
            };
          });
        }),
      )
      .subscribe(
        (transformedCigars) => {
          this.cigarsSearched.next(transformedCigars);
        },
        (error) => {
          console.log(error);
        },
      );
  }
  /**
   * @param humidorId
   * @param offset wie viele Einträge wurden schon geholt
   * @param amount wie viele Einträge pro Request an REST
   * @description
   * Methode holt den Inhalt des jeweiligen Humidors aus der DB, mappt die Objekte auf
   * das entsprechende Zigarren-"Interface"
   */
  public async getHumidorContents(
    humidorId: number,
    offset: number = 0,
    amount: number = 1000,
  ) {
    if (offset === 0) {
      this.humEntries = [];
      this.percLoaded.next(0);
      //Popover für Anzeige des Ladestatus in Prozent
      this.loadingPop = await this.popc.create({
        component: HumidorPopoverLoadingComponent,
        componentProps: { loaded: this.percLoaded.asObservable() },
        backdropDismiss: false,
        id: "loadingPop",
      });
      await this.loadingPop.present();
    }
    this.http
      .post<any>(`${this.api}/get_humidor_contents.php`, {
        humId: humidorId,
        offset: offset,
        amount: amount,
      }) //Mapping des JSON-Results auf unsere Interfaces
      .pipe(
        map((result) => {
          //console.log(result);
          return {
            entries: result.humEntries.map((entry) => {
              /*passende Getränke und Aromen sind entweder JSON-Strings
              oder null, null wird durch leeres Array getauscht, für
              Pipes etc
              */
              entry.drinks = JSON.parse(entry.drinks);
              entry.aromas = JSON.parse(entry.aromas);
              if (entry.drinks === null) {
                entry.drinks = [];
              }
              if (entry.aromas === null) {
                entry.aromas = [];
              }
              return {
                id: entry.humentry_id,
                humidorId: humidorId,
                cigar: {
                  id: entry.cigar_id,
                  name: entry.name,
                  brand: {
                    id: entry.brand_id,
                    name: entry.brand_name,
                    since: 0,
                  },
                  length: entry.length,
                  diameter: entry.diameter,
                  construction: entry.construction, //Enum in DB longfiller, shortfiller, mediumfiller
                  shape: {
                    id: entry.shape_id,
                    name: entry.shape_name,
                    length_min: 0, //folgende 4 Werte sind nicht erforderlich, werden auch nicht aus DB geholt
                    length_max: 0,
                    diameter_min: 0,
                    diameter_max: 0,
                  },
                  ring: entry.ring,
                  origin: entry.origin,
                },
                cigarAmount: entry.amount,
                storedDate: entry.date,
                pricePerCigar: entry.price, //in Ct
                cigarStrength: entry.strength, //1-6
                smokingDuration: entry.duration,
                fittingDrinks: entry.drinks,
                cigarAromas: entry.aromas,
                ripingTime: this.calculateMonthDifference(
                  Date.parse(entry.date),
                ),
                rating: entry.rating,
                location: entry.location,
                annotation: entry.annotation,
                picture: entry.picture,
              };
            }),
            complete: result.complete,
            entry_count: result.total,
          };
        }),
      )
      .subscribe(
        async (transformedAnswer) => {
          //console.log(this.humEntries)
          //console.log("Hum Inhalt geholt")
          //ALLE Einträge wurde geladen
          if (transformedAnswer.complete === true) {
            this.humEntries = this.humEntries.concat(transformedAnswer.entries);
            this.percLoaded.next(1);
            this.entriesUpdated.next(this.humEntries);
            await this.loadingPop.dismiss();
          }
          //Ein Teil der Einträge (amount) wurde geladen
          else {
            this.humEntries = this.humEntries.concat(transformedAnswer.entries);
            this.getHumidorContents(humidorId, offset + 1000);
            this.percLoaded.next(
              this.percLoaded.getValue() +
                amount / transformedAnswer.entry_count,
            );
            this.entriesUpdated.next(this.humEntries);
          }
        },
        async (error) => {
          //Kein Zugriff auf Humidor
          if (error.status === 401) {
            console.log(error);
            await this.loadingPop.dismiss();
            switch (error.error.message) {
              case "unauthorized":
                await this.ls.presentMessage(
                  "humidor-service.not-allowed",
                  2000,
                  "error",
                );
                this.router.navigate(["somewhere"]);
                await this.ls.hideLoading();
                break;
              case "subscription exceeded":
                //wenn Abo abgelaufen ist
                await this.ls.presentMessage(
                  "humidor-service.subscription-exceeded",
                  5000,
                  "error",
                );
                setTimeout(async () => {
                  this.router.navigate(["folder/profile"]);
                  await this.ls.hideLoading();
                }, 5000);

                break;
            }
          }
        },
      );
  }
  /**
   * @param humidorId
   * @param entry
   * @param newAmount
   * @param oldAmount
   * @param silent sollen Meldungen ausgegeben werden (bei Verlegung lieber nicht)
   * @description
   * Anzahl bestimmter Zigarren im Humidor verändern/löschen
   * und View aktualisieren
   * @returns
   */
  public async updateHumidorEntryAmount(
    humidorId: number,
    entry: HumidorEntry,
    newAmount: number,
    oldAmount: number,
    silent: boolean = false,
  ): Promise<boolean> {
    let updated: boolean = true;
    /*Wir fragen den Nutzer, ob er wirklich löschen möchte und weisen
    auf Attribut-Verlust hin
    */

    if (silent === false) {
      //Wir verschieben in diesem Fall ja und löschen nicht
      if (newAmount === 0) {
        if (localStorage.getItem("no_warnings") === null) {
          const ynp = await this.popc.create({
            component: YesNoPopupComponent,
            componentProps: { questionType: "really-delete" },
          });
          ynp.present();
          const answer = await ynp.onDidDismiss();
          if (answer.data.answerData.no_messages === true) {
            localStorage.setItem("no_warnings", "true");
          }
          if (answer.data.answerData.answer === false) {
            updated = false;
          }
        }
      }
    }
    if (updated === true) {
      const user = JSON.parse(localStorage.getItem("user"));
      const params = {
        humId: humidorId,
        cigarId: entry.cigar.id,
        cigarAmount: newAmount,
        oldCigarAmount: oldAmount,
        addedDate: entry.storedDate,
      };
      this.ls.presentLoading().then(() => {
        this.http
          .post<any>(`${this.api}/update_humidorentry_amount.php`, params)
          .subscribe(async (answer) => {
            await this.ls.hideLoading();
            if (answer.message === "deleted") {
              if (silent === false) {
                this.ls.presentMessage(
                  "humidor-service.entry-removed",
                  2000,
                  "success",
                );
              }
              //View Update ohne DB-Anfrage
              for (let i = 0; i < this.humEntries.length; i++) {
                if (this.humEntries[i].id === entry.id) {
                  this.humEntries.splice(i, 1);
                  this.entriesUpdated.next(this.humEntries);
                  break;
                }
              }
              updated = true;
            }
            if (answer.message === "updated") {
              if (silent === false) {
                await this.ls.presentMessage(
                  "humidor-service.amount-changed",
                  2000,
                  "success",
                );
              }
              //View Update ohne DB-Anfrage
              for (let i = 0; i < this.humEntries.length; i++) {
                if (this.humEntries[i].id === entry.id) {
                  this.humEntries[i].cigarAmount = newAmount;
                  this.entriesUpdated.next(this.humEntries);
                  break;
                }
              }
              updated = true;
            }
            if (answer.message === "hum_capacity exceeded!") {
              await this.ls.presentMessage(
                "addcigar-form.capacity-exceeded",
                3500,
                "error",
              );
              updated = false;
            }
          });
      });
    }
    return updated;
  }

  /**
   * @param oldhumId ID des aktuellen Humis
   * @param newhumId ID des neuen Humis
   * @param amountToMove Wieviel soll verschoben werden
   * @param humEntry kompletter Humidor-Eintrag
   * @description
   * Verschiebt bestimmte Anzahl von Zigarren von einem Humi
   * in den anderen.
   */
  public async moveHumEntry(
    newhumId: number,
    amountToMove: number,
    humEntry: HumidorEntry,
  ) {
    /*Zunächst Transfer versuchen*/
    const newAmount = humEntry.cigarAmount - amountToMove; //Humidor 1
    const oldAmount = humEntry.cigarAmount; //Humidor 1
    humEntry.cigarAmount = amountToMove; /*Humidor 2,
    Achtung Angular aktualisiert tatsächlich die View, ändert man
    was am Eintrag*/
    const moved = await this.addEntry(newhumId, humEntry, false);
    if (moved === true) {
      await this.ls.presentMessage(
        "humidor-service.entry-moved",
        2000,
        "success",
      );
      //Anzahl reduzieren
      const reduced = await this.updateHumidorEntryAmount(
        humEntry.humidorId,
        humEntry,
        newAmount,
        oldAmount,
        true,
      );
      if (reduced === true) {
        humEntry.cigarAmount = newAmount;
      }
    } else {
      //Menge zurücksetzen für View
      humEntry.cigarAmount = oldAmount;
    }
  }

  //gibt Subject als abbonierbares Observable zurück
  public getEntriesUpdateListener() {
    return this.entriesUpdated.asObservable();
  }
  //gibt Subject als abbonierbares Observable zurück
  public getCigarsUpdateListener() {
    return this.cigarsSearched.asObservable();
  }

  /**
   * @param humidorId
   * @param entry: Humidor-Eintrag mit Zigarren-Objekt
   * @param updateView: bei einem Transfer in einen anderen Humidor, wollen
   * wir die View nicht aktualisieren, weil wir sonst hier den Inhalt und auch
   * sonstige Attribute des anderen Humidors erhalten
   * @description
   * Bereits bekannte Zigarre in Humidor legen Danach wieder Humidor
   * Inhalt holen (nur wenn Aufruf für aktuell angezeigeten
   * Humidor(Komponent))
   */
  public async addEntry(
    humidorId: number,
    entry: HumidorEntry,
    updateView = true,
  ): Promise<boolean> {
    let added = false;
    let oldentryId = 0;
    if (entry.picture == true) {
      oldentryId = entry.id;
    }
    const params = {
      humId: humidorId,
      cigarId: entry.cigar.id,
      cigarPrice: entry.pricePerCigar,
      cigarAmount: entry.cigarAmount,
      cigarDuration: entry.smokingDuration,
      cigarStrength: entry.cigarStrength,
      addedDate: entry.storedDate,
      fittingDrinks: entry.fittingDrinks,
      fittingAromas: entry.cigarAromas,
      location: entry.location,
      annotation: entry.annotation,
      picture: entry.picture,
      oldentryId: oldentryId,
    };
    /*Wir wandeln die Subscription in ein Promise um,
    damit wir bei Erfolg etwas zurück geben können*/
    await this.ls.presentLoading();
    const answer = await this.http
      .post<any>(`${this.api}/add_humidor_contents.php`, params)
      .toPromise()
      .catch((error) => {
        console.log(error);
        if (error.status === 409) {
          this.ls.presentMessage(
            "addcigar-form.capacity-exceeded",
            3500,
            "error",
          );
          added = false;
        }
      });
    if (typeof answer !== "undefined") {
      if (updateView === true) {
        //Es wurde ein neuer Eintrag hinzugefügt
        if (answer.message === "entry added") {
          entry.id = answer.entry_id;
          entry.humidorId = humidorId;
          console.log(entry);
          //View Update ohne DB-Anfrage
          //this.getHumidorContents(humidorId);
          //Reifezeit hier im Service ermitteln (Form gibt nur Datum-String zurück)
          entry.ripingTime = this.calculateMonthDifference(
            Date.parse(entry.storedDate),
          );
          let added = false;
          for (let i = 0; i < this.humEntries.length; i++) {
            //Alphabetischer Vergleich
            if (
              this.humEntries[i].cigar.name.localeCompare(entry.cigar.name) ===
              1
            ) {
              this.humEntries.splice(i, 0, entry);
              added = true;
              break;
            }
          }
          //Wenn noch nicht eingefügt, dann muss es ans Ende
          if (added === false) {
            this.humEntries.splice(this.humEntries.length, 0, entry);
          }
          this.ls.presentMessage(
            "humidor-service.entry-added",
            2000,
            "success",
          );
        }
        //Es war schon eine Zigarre mit gleicher Id UND gleichem Datum vorhanden
        else if (answer.message === "entry updated") {
          entry.id = answer.entry_id;
          for (let i = 0; i < this.humEntries.length; i++) {
            if (this.humEntries[i].id === entry.id) {
              this.humEntries[i].cigarAmount += entry.cigarAmount;
            }
          }
          this.ls.presentMessage(
            "humidor-service.amount-changed",
            2000,
            "success",
          );
        }
        this.entriesUpdated.next(this.humEntries);
      }
      added = true;
    }
    await this.ls.hideLoading();
    return added;
  }

  /**
   * Neue Zigarre in Humidor anlegen
   * @param humidorId
   * @param humEntry
   *
   */
  public async createNewCigar(humidorId: number, humEntry: HumidorEntry) {
    //console.log("this Cigar will be created: ");
    //console.log(cigar);
    let cigar: Cigar = humEntry.cigar;
    this.ls.presentLoading().then(() => {
      this.http.post<any>(`${this.api}/new_cigar.php`, { cigar }).subscribe(
        (res) => {
          /* Wird ohnehin nicht passieren. Neue Zigarren nur auf Antrag
          und Freigabe durch Admin
          humEntry.cigar.id=res;
          this.addEntry(humidorId, humEntry);*/
        },
        async (error) => {
          //Fehler tritt IMMER auf
          switch (error.status) {
            case 403: //Forbidden
              let requestType: string = error.error.error; //das stimmt schon so
              const popover = await this.popc.create({
                component: YesNoPopupComponent,
                componentProps: { questionType: requestType },
                backdropDismiss: false,
              });
              await popover.present();
              await this.ls.hideLoading();
              const answer = await popover.onDidDismiss();
              /*Benutzer möchte Antrag stellen -> Mail an Admin mit Link zur
              Adminoberfläche inkl. beantragter Daten
              */
              if (answer.data.answer === true) {
                this.sendDataRequestMail(humidorId, humEntry, requestType);
              }
          }
        },
      );
    });
    await this.ls.hideLoading();
  }
  /**
   * @param humidorId
   * @param oldCigar
   * @param newCigar
   * @description
   * Wenn eine bereits existierende Zigarre hinzugefügt werden soll wird
   * dass gemacht und anschließend der alte Eintrag entfernt.
   * Bei einer NEUEN Zigarre wird lediglich die Antragsmail verschickt.
   */
  public async updateHumidorEntry(
    humidorId: number,
    oldEntry: HumidorEntry,
    newEntry: HumidorEntry,
  ) {
    await this.ls.presentLoading();
    if (newEntry.cigar.id === 0) {
      //Neue Zigarre?
      await this.updateHumidorEntryAmount(
        humidorId,
        oldEntry,
        0,
        oldEntry.cigarAmount,
        true,
      );
      this.createNewCigar(humidorId, newEntry);
    } else {
      //Für Löschen andere Methode aufrufen
      if (newEntry.cigarAmount === 0) {
        await this.updateHumidorEntryAmount(
          humidorId,
          newEntry,
          0,
          oldEntry.cigarAmount,
        );
        await this.ls.hideLoading();
      } else {
        this.http
          .post<any>(`${this.api}/update_humidorentry.php`, {
            oldentry: oldEntry,
            newentry: newEntry,
          })
          .subscribe({
            next: async (answer) => {
              if (answer.message === "updated") {
                this.ls.presentMessage(
                  "humidor-service.entry-changed",
                  2000,
                  "success",
                );
                //Reifezeit hier im Service ermitteln (Form gibt nur Datum-String zurück)
                newEntry.ripingTime = this.calculateMonthDifference(
                  Date.parse(newEntry.storedDate),
                );
                //Update des Eintrags ohne Reload aus DB
                //Die gute alte for Schleife lässt sich auch abbrechen im Gegensatz zu forEach
                for (let i = 0; i < this.humEntries.length; i++) {
                  if (this.humEntries[i].id === oldEntry.id) {
                    newEntry.id = oldEntry.id; //wäre sonst 0=>Folgefehler
                    newEntry.humidorId = oldEntry.humidorId; //wäre sonst 0=>Folgefehler
                    this.humEntries[i] = cloneDeep(newEntry);
                    this.entriesUpdated.next(this.humEntries);
                    break;
                  }
                }
              }
              await this.ls.hideLoading();
            },
            error: async (error) => {
              if (error.status === 409) {
                await this.ls.presentMessage(
                  "addcigar-form.capacity-exceeded",
                  3500,
                  "error",
                );
              }
            },
          });
      }
    }
  }

  /**
   *
   * @param past Boxing Date oder Einlagerungsdatum in Millisekungen seit 1.1.1970
   * @returns Anzahl der Monate, die die Zigarre eingelagert oder geboxed wurde
   */
  private calculateMonthDifference(past: number): number {
    let monthDiff: number;
    const today = Date.now();
    var diff = today - past;
    monthDiff = Math.round((diff / 1000 / 60 / 60 / 24 / 365) * 12);
    return monthDiff;
  }

  /**
   *
   * @param humidorId: Humidor-ID des Nutzers
   * @param humEntry: Egal ob neue oder Änderung Zigarre, Marke, Format => komplette Daten versenden
   *
   */
  private sendDataRequestMail(humidorId, humEntry, requestType) {
    if (localStorage.getItem("user") !== null) {
      let userMail = JSON.parse(localStorage.getItem("user")).email;
      this.http
        .post<any>(`${this.api}/send_data_request_mail.php`, {
          cigarData: humEntry,
          humidorId: humidorId,
          userMail: userMail,
          requestType: requestType,
        })
        .subscribe((answer) => {
          if (answer.drq_mail_sent === true) {
            this.ls.presentMessage(
              "displayloading-service.drq_mail_sent",
              5000,
              "success",
            );
          }
        });
    }
  }

  /**
   *
   * @param humidors
   * Ruft API auf um ggf. Änderungen an den Humidoren
   * des Benutzers durchzuführen
   *
   *
   */
  public async changeHumis(humidors: Humidor[]): Promise<boolean> {
    const answer = await this.http
      .post<any>(`${this.api}/change_humidors.php`, { humidors: humidors })
      .toPromise()
      .catch((error) => {
        return false;
      });
    if (answer.message === "humis_changed") {
      if (typeof answer.new_humis !== "undefined") {
        this.as.changeUserHumis(JSON.parse(answer.new_humis));
      } else {
        this.as.changeUserHumis(humidors);
      }
      return true;
    } else {
      return false;
    }
  }
  /**
   * Auf nochmalige Nachfrage hin Backend zum Humidor löschen aufrufen
   * @param humiId
   * @returns
   */
  public async deleteHumi(humiId: number): Promise<boolean> {
    let deleted: boolean;
    const ynp = await this.popc.create({
      component: YesNoPopupComponent,
      componentProps: { questionType: "really-delete-humi" },
    });
    ynp.present();
    const answer = await ynp.onDidDismiss();
    if (answer.data.answer !== false) {
      await this.ls.presentLoading();
      const deleteHumi = await this.http
        .post<any>(`${this.api}/delete_humi.php`, { humiId: humiId })
        .toPromise()
        .catch((error) => {
          if (error.status === 401) {
            deleted = false;
            this.ls.presentMessage(
              "humidor-service.not-allowed",
              2000,
              "error",
            );
          }
        });
      if (
        typeof deleteHumi !== "undefined" &&
        deleteHumi.message === "humi_deleted"
      ) {
        deleted = true;
        this.ls.presentMessage(
          "displayloading-service.humi-deleted",
          2000,
          "success",
        );
      }
      await this.ls.hideLoading();
    }
    return deleted;
  }
  /**
   * @description
   * Holt alle Zigarren (Name und kumulierte Menge und Menge pro Humidor) aus allen Humidoren kumuliert
   * @returns
   * JSON ARRAY
   */
  public async getUberblick() {
    await this.ls.presentLoading();
    const getUberblick = await this.http.post<any>(
      `${this.api}/uberblick.php`,
      "",
    );
    const allCigars = await lastValueFrom(getUberblick);
    let totalcigars = allCigars.totalcigars;
    totalcigars.forEach((entry) => {
      entry.amount_per_humis = JSON.parse(entry.amount_per_humis);
    });
    await this.ls.hideLoading();
    return allCigars.totalcigars;
  }

  /**
   * @description
   * Holt die genutzte Kapazität eines definierten Humidors anhand seiner ID
   * @params
   * humId: ID des Humidors
   * @returns
   * number: genutzte Kapazität
   */
  public async getUsedHumiCap(humId: number): Promise<number> {
    const getUsedCap = await this.http.post<any>(
      `${this.api}/get_used_humicap.php`,
      { humid: humId },
    );
    const usedCap = await lastValueFrom(getUsedCap);
    return parseInt(usedCap.usedcap.used_capacity);
  }

  /**
   * @description
   * CSV-Export aller Humidore des jeweiligen Nutzers
   * Nur für Premium und Ultra- Nutzer
   * @params
   * @returns
   */
  public async getHumidorsContentsCSV(): Promise<boolean> {
    const getAllCigars = await this.http.get<any>(
      `${this.api}/get_humi_csv.php`,
    );
    const allCigars = await lastValueFrom(getAllCigars);
    if (
      typeof allCigars.success !== "undefined" &&
      allCigars.success === "csv sent"
    ) {
      return true;
    } else {
      return false;
    }
  }
}
